import type {ProductSearchFormProps} from "./ProductSearchForm";
import AddAddressForm from "./AddAddressForm";
import {useEffect, useState} from "react";
import {confirmable, type ConfirmDialogProps, createConfirmation} from "react-confirm";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSearchParams } from 'react-router-dom';
import SkeletonLoader from './SkeletonLoader';
import MyAccountPageShell from './MyAccountPageShell';
import { applyWooCartUpdates } from '../utils/applyWooCartUpdates';

export interface Customer {
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
    company: string;
    country: string;
    state: string;
}

export interface Address {
    id: number | string;
    nickname?: string;
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
    company?: string;
    address1: string;
    address2?: string;
    postcode: string;
    city: string;
    region: string;
    country: string;
    default_billing: boolean;
    default_shipping: boolean;
}

export interface AddressManagementProps {
    addresses: Address[],
    customer: Customer,
    countries: CountryOption
    states: StateOption
    wekaPagePermissions?: Record<string, boolean>
}

export interface CountryOption {
    [code: string]: string;
}

export interface StateOption {
    [countryCode: string]: { [stateCode: string]: string };
}

const isAddressFlagEnabled = (value: unknown): boolean => (
    value === true || value === 1 || value === '1'
);

const normalizeAddress = (address: Address): Address => ({
    ...address,
    default_billing: isAddressFlagEnabled(address.default_billing),
    default_shipping: isAddressFlagEnabled(address.default_shipping),
});

const shouldRefreshWooFragmentsForCurrencyChange = (): boolean => {
    const runtimeWindow = window as any;
    const pricingSettings = runtimeWindow?.wekaApiSettings?.pricing;
    if (pricingSettings?.currency_pricing_enabled !== true) {
        return false;
    }

    const hasWooFragmentRuntime = typeof runtimeWindow?.jQuery === 'function'
        && (
            typeof runtimeWindow?.wc_cart_fragments_params !== 'undefined'
            || typeof runtimeWindow?.woocommerce_params !== 'undefined'
        );

    if (hasWooFragmentRuntime) {
        return true;
    }

    return Boolean(
        document.querySelector(
            '.widget_shopping_cart_content, .widget_shopping_cart, .elementor-widget-woocommerce-menu-cart, .elementor-menu-cart__container, .elementor-menu-cart__main'
        )
    );
};

const refreshWooFragmentsForCurrencyChange = (): void => {
    const jqueryInstance = (window as any)?.jQuery;
    if (typeof jqueryInstance !== 'function') {
        return;
    }

    jqueryInstance(document.body).trigger('wc_fragment_refresh');
};

const AddressManagement: React.FC<Partial<AddressManagementProps>> = ({
    addresses: initialAddresses,
    customer: initialCustomer,
    countries: initialCountries,
    states: initialStates,
    wekaPagePermissions = {},
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const editId = searchParams.get('id');
    const [addressList, setAddressList] = useState<Address[]>((initialAddresses || []).map(normalizeAddress));
    const [customer, setCustomer] = useState<Customer | null>(initialCustomer || null);
    const [countries, setCountries] = useState<CountryOption>(initialCountries || {});
    const [states, setStates] = useState<StateOption>(initialStates || {});
    const [loading, setLoading] = useState(!initialAddresses);
    const [editAddress, setEditAddress] = useState<Address | null>(null);
    const [permissionNotice, setPermissionNotice] = useState<string | null>(null);
    const [pendingDefaultUpdate, setPendingDefaultUpdate] = useState<string | null>(null);
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [ignoreEditParam, setIgnoreEditParam] = useState(false);
    const canAddAddress = wekaPagePermissions.add_address !== false;
    const canEditAddress = wekaPagePermissions.edit_address !== false;
    const canDeleteAddress = wekaPagePermissions.delete_address !== false;
    const canSetDefaults = wekaPagePermissions.set_default_billing_shipping !== false;

    useEffect(() => {
        // If data wasn't provided as props, fetch it
        if (!initialAddresses || !initialCustomer || !initialCountries) {
            fetchAddressData();
        }

        const params = new URLSearchParams(window.location.search);
        if (params.get('success') === 'true') {
            toast('New address added!', {type: "success"})
        }
    }, []);

    useEffect(() => {
        if (!editId) {
            setIgnoreEditParam(false);
            setEditAddress(null);
            return;
        }

        if (ignoreEditParam) {
            return;
        }

        // Set edit address when editId changes
        if (editId && addressList.length > 0) {
            if (!canEditAddress) {
                setPermissionNotice('Editing addresses is disabled for this page.');
                setSearchParams({}, { replace: true });
                setEditAddress(null);
                return;
            }

            const address = addressList.find(addr => String(addr.id) === editId);
            setEditAddress(address || null);
            setIsAddressModalOpen(Boolean(address));
        } else {
            setEditAddress(null);
        }
    }, [editId, addressList, canEditAddress, setSearchParams, ignoreEditParam]);

    const fetchAddressData = async () => {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);
            
            const response = await fetch('/wp-json/weka/v1/address/list', {
                headers: {
                    'X-WP-Nonce': (window as any).wekaApiSettings?.nonce || ''
                },
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            if (response.ok) {
                const data = await response.json();
                setAddressList((data.addresses || []).map(normalizeAddress));
                setCustomer(data.customer || null);
                setCountries(data.countries || {});
                setStates(data.states || {});
            }
        } catch (error: any) {
            console.error('Failed to fetch address data:', error);
            if (error.name === 'AbortError') {
                toast('Request timeout - please check your connection', {type: "error"});
            } else {
                toast('Failed to load addresses', {type: "error"});
            }
        } finally {
            setLoading(false);
        }
    };

    const onAddressAdded = (address: Address) => {
        setAddressList([...addressList, normalizeAddress(address)]);
    }

    const onAddressUpdated = (updatedAddress: Address) => {
        setAddressList((prevState) =>
            prevState.map((address) => (
                String(address.id) === String(updatedAddress.id) ? normalizeAddress(updatedAddress) : address
            ))
        );
    };

    const openAddAddressModal = () => {
        if (!canAddAddress) {
            setPermissionNotice('Adding addresses is disabled for this page.');
            return;
        }

        setIgnoreEditParam(false);
        setEditAddress(null);
        setSearchParams({}, { replace: true });
        setIsAddressModalOpen(true);
    };

    const openEditAddressModal = (address: Address) => {
        if (!canEditAddress) {
            setPermissionNotice('Editing addresses is disabled for this page.');
            return;
        }

        setIgnoreEditParam(false);
        setEditAddress(address);
        setSearchParams({ id: String(address.id) }, { replace: true });
        setIsAddressModalOpen(true);
    };

    const closeAddressModal = () => {
        setIgnoreEditParam(true);
        setEditAddress(null);
        setSearchParams({}, { replace: true });
        setIsAddressModalOpen(false);
    };

    const removeAddress = (addressId: string | number) => {
        setAddressList((prevState) => {
            return prevState.filter((itrAddr) => itrAddr.id !== addressId)
        })
    }

    const MyDialog = ({ show, proceed, message }: ConfirmDialogProps<{ message: string }, boolean>) => (
        <div className={`dialog-overlay ${show ? 'show' : 'hide'}`}>
            <div className="dialog">
                <p>{message}</p>
                <button className={'button weka-primary-btn'} onClick={() => proceed(true)}>Yes</button>
                <button className={'button weka-primary-btn'} onClick={() => proceed(false)}>No</button>
            </div>
        </div>
    );

    const confirm = createConfirmation(confirmable(MyDialog));

    const onConfirmAddressDelete = async (address: Address)=> {
        if (!canDeleteAddress) {
            setPermissionNotice('Deleting addresses is disabled for this page.');
            return;
        }

        await fetch('/wp-json/weka/v1/address/delete', {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': wekaApiSettings.nonce
            },
            body: JSON.stringify({ id: address.id }),
        }).then(async (res) => {
            if (res.status === 403) {
                let data = await res.json()
                toast(data.message, {type: "error"})
            } else {
                removeAddress(address.id)
                let data = await res.json()
                toast(data.message, {type: "success"})
            }
        }).catch((err) => {
            console.log(err)
        });
    }

    const updateDefaultBillingShipping = async (id: string | number, type: 'billing' | 'shipping') => {
        if (!canSetDefaults) {
            setPermissionNotice('Setting default billing/shipping is disabled for this page.');
            return;
        }

        const pendingKey = `${id}:${type}`;
        if (pendingDefaultUpdate === pendingKey) {
            return;
        }

        setPendingDefaultUpdate(pendingKey);

        try {
            const response = await fetch('/wp-json/weka/v1/address/switch', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': wekaApiSettings.nonce
                },
                body: JSON.stringify({ id: id, type: type })
            });

            if (response.ok) {
                const data = await response.json();
                toast(data.message, {type: "success"});

                const selectedAddress = addressList.find((addr) => String(addr.id) === String(id));
                const previousDefaultAddress = addressList.find((addr) => (
                    type === 'billing' ? addr.default_billing : addr.default_shipping
                ));
                const countryChanged = Boolean(
                    selectedAddress
                    && previousDefaultAddress
                    && String(selectedAddress.id) !== String(previousDefaultAddress.id)
                    && selectedAddress.country !== previousDefaultAddress.country
                );
                const pricingCountrySource = String((window as any)?.wekaApiSettings?.pricing?.multi_currency_country_source || '').trim();

                // Update state immutably
                setAddressList(prevState => {
                    return prevState.map(addr => {
                        // Create new object to ensure React detects change
                        return {
                            ...addr,
                            default_billing: type === 'billing' 
                                ? (addr.id == id) 
                                : addr.default_billing,
                            default_shipping: type === 'shipping' 
                                ? (addr.id == id) 
                                : addr.default_shipping
                        };
                    });
                });

                if (
                    countryChanged
                    && pricingCountrySource === type
                    && data?.fragments
                ) {
                    applyWooCartUpdates({
                        fragments: data.fragments,
                        cart_hash: data.cart_hash,
                        cart_count: data.cart_count,
                    });
                } else if (
                    countryChanged
                    && pricingCountrySource === type
                    && shouldRefreshWooFragmentsForCurrencyChange()
                ) {
                    refreshWooFragmentsForCurrencyChange();
                }
            } else {
                const error = await response.json();
                toast(error.message || 'Failed to update default address', {type: "error"});
            }
        } catch (error) {
            console.error('Error updating default address:', error);
            toast('Failed to update default address', {type: "error"});
        } finally {
            setPendingDefaultUpdate(null);
        }
    }

    const getCountryLabel = (countryCode: string) => {
        if (!countryCode) {
            return '';
        }

        return countries[countryCode] || countryCode;
    };

    const getRegionLabel = (countryCode: string, regionCode: string) => {
        if (!regionCode) {
            return '';
        }

        const countryStates = states[countryCode as keyof typeof states];
        if (countryStates && countryStates[regionCode]) {
            return countryStates[regionCode];
        }

        return regionCode;
    };

    const getAddressMeta = (address: Address) => {
        const normalizePart = (value: string | undefined) => String(value || '').trim();
        const locationParts = [
            normalizePart(address.address1),
            normalizePart(address.address2),
            [
                normalizePart(address.city),
                normalizePart(getRegionLabel(address.country, address.region)),
                normalizePart(address.postcode),
            ].filter((value) => value !== '').join(', '),
            normalizePart(getCountryLabel(address.country)),
        ].filter((value) => value !== '');

        return locationParts;
    };

    if (loading) {
        return (
        <MyAccountPageShell
            title="Address Management"
            subtitle="Manage billing, shipping, and saved delivery addresses."
            actions={canAddAddress ? (
                <button
                    type="button"
                    className="button"
                    onClick={openAddAddressModal}
                >
                    Add Address
                </button>
            ) : undefined}
        >
                    <SkeletonLoader type="title" />
                    <div className="row mt-4">
                        <div className="col-md-6 mb-4">
                            <div className="skeleton" style={{ height: '200px' }} />
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="skeleton" style={{ height: '200px' }} />
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="skeleton" style={{ height: '200px' }} />
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="skeleton" style={{ height: '200px' }} />
                        </div>
                    </div>
            </MyAccountPageShell>
        );
    }

    if (!customer) {
        return (
            <MyAccountPageShell
                title="Address Management"
                subtitle="Manage billing, shipping, and saved delivery addresses."
            >
                    <div className="alert alert-danger" role="alert">
                        Unable to load customer data
                    </div>
                    <button onClick={fetchAddressData} className="btn btn-primary">Retry</button>
            </MyAccountPageShell>
        );
    }

    return <MyAccountPageShell
        title="Address Management"
        subtitle="Manage billing, shipping, and saved delivery addresses."
        actions={canAddAddress ? (
            <button
                type="button"
                className="button"
                onClick={openAddAddressModal}
            >
                Add Address
            </button>
        ) : undefined}
    >
            {permissionNotice && (
                <div className="alert alert-warning" role="alert">
                    {permissionNotice}
                </div>
            )}
            <div className={'address-management'}>
                <div className="portal-panel address-panel p-2">
                    <div className="portal-panel-container">
                        <div className="address-list">
                            {addressList.length < 1 && (
                                <div className="empty-state">
                                    <p>Looks like you haven't added any other addresses yet.</p>
                                </div>
                            )}
                            {addressList.map(address => (
                                <div
                                    key={address.id}
                                    className={[
                                        'address-row',
                                        'mb-3',
                                        address.default_billing || address.default_shipping ? 'address-row--is-default' : '',
                                        address.default_billing ? 'address-row--billing-default' : '',
                                        address.default_shipping ? 'address-row--shipping-default' : '',
                                    ].filter(Boolean).join(' ')}
                                >
                                    <div className="address-main">
                                        <div className="address-main__meta">
                                            {address.nickname && address.nickname.trim() !== '' && (
                                                <span className="address-pill address-pill--nickname">{address.nickname}</span>
                                            )}
                                        </div>
                                        <p className="address-name">
                                            {`${address.firstname} ${address.lastname}`.trim()}
                                        </p>
                                        {address.company && <p className="address-company">{address.company}</p>}
                                        <div className="address-location">
                                            {getAddressMeta(address).map((line, index) => (
                                                <p key={`${address.id}-line-${index}`}>{line}</p>
                                            ))}
                                        </div>
                                        {(address.email || address.phone) && (
                                            <div className="address-contact">
                                                {address.email && <p className="address-email">{address.email}</p>}
                                                {address.phone && <p className="address-phone">{address.phone}</p>}
                                            </div>
                                        )}
                                    </div>
                                    <div className="address-side">
                                        <div className="address-switches">
                                            <div className="address-switch-row">
                                                <span className="address-switch-row__label">Default Billing</span>
                                                <button
                                                    type="button"
                                                    className="address-switch-button"
                                                    onClick={() => canSetDefaults && updateDefaultBillingShipping(address.id, 'billing')}
                                                    aria-label="Set as default billing address"
                                                    disabled={!canSetDefaults || pendingDefaultUpdate === `${address.id}:billing`}
                                                >
                                                    <span className={'address-switch' + (address.default_billing ? ' is-on' : '') + (pendingDefaultUpdate === `${address.id}:billing` ? ' is-pending' : '')}>
                                                        {pendingDefaultUpdate === `${address.id}:billing` && (
                                                            <span className="address-switch__spinner" aria-hidden="true"></span>
                                                        )}
                                                    </span>
                                                </button>
                                            </div>
                                            <div className="address-switch-row">
                                                <span className="address-switch-row__label">Default Shipping</span>
                                                <button
                                                    type="button"
                                                    className="address-switch-button"
                                                    onClick={() => canSetDefaults && updateDefaultBillingShipping(address.id, 'shipping')}
                                                    aria-label="Set as default shipping address"
                                                    disabled={!canSetDefaults || pendingDefaultUpdate === `${address.id}:shipping`}
                                                >
                                                    <span className={'address-switch' + (address.default_shipping ? ' is-on' : '') + (pendingDefaultUpdate === `${address.id}:shipping` ? ' is-pending' : '')}>
                                                        {pendingDefaultUpdate === `${address.id}:shipping` && (
                                                            <span className="address-switch__spinner" aria-hidden="true"></span>
                                                        )}
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                        <div className="address-actions">
                                            {canEditAddress && (
                                                <button
                                                    type="button"
                                                    onClick={() => openEditAddressModal(address)}
                                                    className="button address-action-button address-action-button--edit"
                                                >
                                                    <i className="fas fa-pen" aria-hidden="true"></i>
                                                    <span>Edit</span>
                                                </button>
                                            )}
                                            {canDeleteAddress && (
                                                <button
                                                    className="address-action-button address-action-button--icon"
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        confirm({
                                                            message: 'Are you sure you want to delete this address?'
                                                        }).then((confirm: boolean) => {
                                                            if (confirm) {
                                                                onConfirmAddressDelete(address)
                                                            }
                                                        })
                                                    }}
                                                    aria-label="Delete address"
                                                    type="button"
                                                >
                                                    <i className="fas fa-trash-alt" aria-hidden="true"></i>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <AddAddressForm
                isOpen={isAddressModalOpen}
                onClose={closeAddressModal}
                customer={customer}
                countries={countries}
                states={states}
                onAddressAdded={onAddressAdded}
                onAddressUpdated={onAddressUpdated}
                editAddress={editAddress}
                editAddressId={editAddress ? String(editAddress.id) : null}
                canAddAddress={canAddAddress}
                canEditAddress={canEditAddress}
            />
    </MyAccountPageShell>
}

export default AddressManagement;
